import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

import { cn } from '#/shared/lib/utils'
import { ChevronDownIcon } from '#/shared/ui/icons'

import { ActivityHistoryAvatar } from './activity-history-avatar'
import { getActorDisplay } from '../model/actor-display'
import type {
  ActivityHistoryChange,
  ActivityHistorySessionEntry,
} from '../model/types'

const TIME_FORMAT = 'h:mm A'
const COLLAPSED_CHANGE_LIMIT = 3

interface ActivityHistorySessionCardProps {
  entry: ActivityHistorySessionEntry
}

function ChangeRow({ change }: { change: ActivityHistoryChange }) {
  return (
    <div className="flex items-start gap-4 py-1.25">
      <div className="flex w-45 shrink-0 items-center gap-1.5">
        {change.pinned && (
          <span aria-hidden className="size-1.5 rounded-full bg-orange-400" />
        )}
        <p className="text-xs uppercase text-gray-500">{change.field}</p>
      </div>
      <p className="min-w-0 flex-1 text-sm text-gray-500">{change.oldValue}</p>
      <p className="min-w-0 flex-1 text-sm font-medium text-gray-900">
        {change.newValue}
      </p>
    </div>
  )
}

function ChangesTable({ changes }: { changes: ActivityHistoryChange[] }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col pl-10">
      <div className="flex items-center gap-4 border-b border-gray-200 pb-2">
        <p className="w-45 shrink-0 text-[10px] uppercase tracking-wider text-gray-500">
          {t('activityHistory.columns.field')}
        </p>
        <p className="min-w-0 flex-1 text-[10px] uppercase tracking-wider text-gray-500">
          {t('activityHistory.columns.oldValue')}
        </p>
        <p className="min-w-0 flex-1 text-[10px] uppercase tracking-wider text-gray-500">
          {t('activityHistory.columns.newValue')}
        </p>
      </div>
      {changes.map((change) => (
        <ChangeRow key={change.id} change={change} />
      ))}
    </div>
  )
}

interface CollapseToggleProps {
  collapsed: boolean
  onToggle: () => void
}

function CollapseToggle({ collapsed, onToggle }: CollapseToggleProps) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex shrink-0 cursor-pointer items-center gap-1 text-sm text-primary hover:underline"
    >
      {collapsed ? t('activityHistory.expand') : t('activityHistory.collapse')}
      <ChevronDownIcon
        className={cn(
          'size-2.5 transition-transform duration-200',
          !collapsed && 'rotate-180',
        )}
      />
    </button>
  )
}

export function ActivityHistorySessionCard({
  entry,
}: ActivityHistorySessionCardProps) {
  const { t } = useTranslation()
  const { user, occurredAt, changes, resourceLabel } = entry
  const [collapsed, setCollapsed] = useState(false)
  const actor = getActorDisplay(user)
  const time = dayjs(occurredAt).format(TIME_FORMAT)
  const showCollapseToggle = changes.length > COLLAPSED_CHANGE_LIMIT
  const visibleChanges =
    showCollapseToggle && collapsed
      ? changes.slice(0, COLLAPSED_CHANGE_LIMIT)
      : changes

  return (
    <div className="flex w-full flex-col gap-3 overflow-hidden rounded-lg border border-gray-200 bg-white px-4 py-3.5">
      <div className="flex items-center gap-3">
        <ActivityHistoryAvatar
          initials={actor.initials}
          tone={actor.tone}
          fallbackId={actor.fallbackId}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1 leading-none">
          <div className="flex items-baseline gap-2 text-sm">
            <span className="font-medium text-gray-900">{actor.name}</span>
            <span className="text-gray-500">{t('activityHistory.edited')}</span>
          </div>
          <p className="text-xs text-gray-500">
            {time} ·{' '}
            {t('activityHistory.changeCount', { count: changes.length })}
            {resourceLabel ? ` · ${resourceLabel}` : ''}
          </p>
        </div>
        {showCollapseToggle && (
          <CollapseToggle
            collapsed={collapsed}
            onToggle={() => setCollapsed((prev) => !prev)}
          />
        )}
      </div>
      <ChangesTable changes={visibleChanges} />
    </div>
  )
}
